'use client';

import { useEffect, useRef, useState } from 'react';

export interface CacheBucketDetails {
    name: string;
    count: number;
    size: string;
}

export interface SyncProgress {
   channel: string;
    completed: number;
    total: number;
    cacheName: string;
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

async function getCacheDetails(): Promise<{ buckets: CacheBucketDetails[]; totalBytes: number }> {
    if (typeof window === 'undefined' || !('caches' in window)) {
        throw new Error('Caches not supported');
    }

    const keys = await caches.keys();
    let totalBytes = 0;
    const buckets: CacheBucketDetails[] = [];

    for (const key of keys) {
        const cache = await caches.open(key);
        const requests = await cache.keys();
        let bucketBytes = 0;

        for (const req of requests) {
            try {
                const res = await cache.match(req);
                if (res) bucketBytes += (await res.clone().blob()).size;
            } catch (error) {
                console.error('Error reading cache item', error);
            }
        }

        totalBytes += bucketBytes;
        buckets.push({ name: key, count: requests.length, size: formatBytes(bucketBytes) });
    }

    return { buckets, totalBytes };
}

async function wipeAllCaches(): Promise<void> {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));

    if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
    }
}

export function useAppCache() {
    const isSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

    const [cacheSize, setCacheSize] = useState<string>('Calculating...');
    const [buckets, setBuckets] = useState<CacheBucketDetails[]>([]);
    const [appState, setAppState] = useState<string>('Checking...');
    const [contentStatus, setContentStatus] = useState<string>('Checking...');
    // One entry per progress channel reported by the service worker (e.g. "app",
    // "images"), each rendered as its own bar.
    const [syncChannels, setSyncChannels] = useState<Record<string, SyncProgress>>({});
    const [isClearing, setIsClearing] = useState(false);

    // Mirror of `syncChannels` for synchronous reads inside the message handler,
    // so we can merge updates monotonically without depending on stale state.
    const channelsRef = useRef<Record<string, SyncProgress>>({});

    // Holds the timer that settles a transient "Out of date" back to "Synchronized"
    // once serwist revalidation has gone quiet. See handleBroadcastUpdate below.
    const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleClearCache = async () => {
        setIsClearing(true);
        try {
            await wipeAllCaches();
            window.location.reload();
        } catch {
            setIsClearing(false);
        }
    };

    useEffect(() => {
        const validateAllCaches = async () => {
            try {
                const { buckets, totalBytes } = await getCacheDetails();
                setBuckets(buckets);
                setCacheSize(formatBytes(totalBytes));
            } catch {
                setCacheSize('Error/Not supported');
            }
        };

        validateAllCaches();
        const livePollingInterval = setInterval(validateAllCaches, 2500);

        if (!isSupported) {
            setAppState('PWA not supported');
            setContentStatus('PWA not supported');
            return () => clearInterval(livePollingInterval);
        }

        const monitorLifecycle = async () => {
            const registration =
                (await navigator.serviceWorker.getRegistration()) ??
                (await navigator.serviceWorker.ready.catch(() => null));

            if (!registration) {
                setAppState('No active service worker');
                setContentStatus('No service worker');
                return;
            }

            if (registration.installing) {
                setAppState('Downloading updates...');
                setContentStatus('Downloading...');
            } else if (registration.waiting) {
                setAppState('New version available');
                setContentStatus('Out of date');
            } else if (registration.active) {
                setAppState('Fully cached');
                setContentStatus((prev) => (prev === 'Checking...' ? 'Synchronized' : prev));
            }

            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                setAppState('Downloading updates...');
                setContentStatus('Downloading...');
                newWorker?.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed') {
                        setAppState('New version available');
                        setContentStatus('Out of date');
                    } else if (newWorker.state === 'activated') {
                        setAppState('Fully cached');
                        setContentStatus('Synchronized');
                        validateAllCaches();
                    }
                });
            });
        };

        monitorLifecycle();

        const handleBroadcastUpdate = (event: MessageEvent) => {
            // One progress stream per channel ("app", "images"), each rendered as
            // its own bar.
            if (event.data?.type === 'SYNC_PROGRESS') {
                const payload = event.data.payload as SyncProgress;
                const prev = channelsRef.current[payload.channel];

                // Merge monotonically: only accept an update that moves a channel
                // forward (more completed) or grows its denominator (a new batch
                // of work). This ignores the stale/empty reports a second,
                // concurrently-updating worker emits during an SW update, so the
                // bars never jump backwards or flicker.
                if (!prev || payload.completed > prev.completed || payload.total > prev.total) {
                    channelsRef.current = { ...channelsRef.current, [payload.channel]: payload };
                    setSyncChannels(channelsRef.current);
                }

                // Derive overall status from the per-channel numbers: synchronized
                // once every reported channel has finished, otherwise syncing.
                const all = Object.values(channelsRef.current);
                const allComplete = all.length > 0 && all.every((c) => c.completed >= c.total);
                if (allComplete) {
                    setContentStatus('Synchronized');
                    validateAllCaches();
                } else {
                    setContentStatus(`Syncing ${payload.cacheName}...`);
                }
                return;
            }

            // A serwist BroadcastUpdate (`CACHE_UPDATED`) fires *after* the cache has
            // already been refreshed with the fresher response — so the cache itself is
            // now in sync; only the currently-rendered page lags by one load (accepted
            // design). Surface "Out of date" transiently, then settle back to
            // "Synchronized" once revalidation goes quiet, so the banner doesn't get
            // stuck out-of-date after caching has actually completed.
            const isSerwistStaleUpdate =
                event.data?.meta === 'serwist-broadcast-update' &&
                event.data?.type === 'CACHE_UPDATED';

            if (isSerwistStaleUpdate) {
                setContentStatus('Out of date');
                validateAllCaches();

                if (settleTimer.current) clearTimeout(settleTimer.current);
                settleTimer.current = setTimeout(async () => {
                    // Keep "Out of date" only if there's a genuinely waiting worker
                    // (a real new version the user must reload to activate).
                    const reg = await navigator.serviceWorker.getRegistration();
                    if (reg?.waiting) return;
                    setContentStatus('Synchronized');
                    validateAllCaches();
                }, 3000);
            }
        };

        navigator.serviceWorker.addEventListener('message', handleBroadcastUpdate);
        return () => {
            clearInterval(livePollingInterval);
            if (settleTimer.current) clearTimeout(settleTimer.current);
            navigator.serviceWorker.removeEventListener('message', handleBroadcastUpdate);
        };
    }, [isSupported]);

    return { isSupported, cacheSize, buckets, appState, contentStatus, syncChannels, isClearing, handleClearCache };
}